import { AlertTriangleIcon, Loader2, XIcon } from "lucide-react";
import { useState } from "react";
import { Button } from "@component/button";
import { Checkbox } from "@component/checkbox";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@component/alert-dialog";

const SUPPRESS_KEY = "candidateDeleteSuppressUntil";
const SUPPRESS_HOURS = 2;

export function isDeleteConfirmSuppressed(): boolean {
    try {
        const until = localStorage.getItem(SUPPRESS_KEY);
        return !!until && Date.now() < Number(until);
    } catch {
        return false;
    }
}

function setDeleteConfirmSuppressed(): void {
    try {
        localStorage.setItem(
            SUPPRESS_KEY,
            String(Date.now() + SUPPRESS_HOURS * 60 * 60 * 1000)
        );
    } catch {}
}

interface DeleteCandidateDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    candidateNames: string[];
    jobTitle: string;
    isLoading?: boolean;
}

export default function DeleteCandidateDialog({
    isOpen,
    onClose,
    onConfirm,
    candidateNames,
    jobTitle,
    isLoading = false,
}: DeleteCandidateDialogProps) {
    const [dontAskAgain, setDontAskAgain] = useState(false);

    const count = candidateNames.length;
    const isSingle = count === 1;

    const handleConfirm = () => {
        if (isLoading) return;
        if (dontAskAgain) {
            setDeleteConfirmSuppressed();
        }
        onConfirm();
    };

    const handleClose = () => {
        if (isLoading) return;
        setDontAskAgain(false);
        onClose();
    };

    return (
        <AlertDialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
            <AlertDialogContent className="max-w-md">
                <AlertDialogHeader>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-red-100 flex-shrink-0">
                                <AlertTriangleIcon className="h-[18px] w-[18px] text-red-500" />
                            </div>
                            <AlertDialogTitle className="text-base font-semibold text-gray-900">
                                {isSingle
                                    ? "Delete this candidate?"
                                    : `Delete ${count} candidates?`}
                            </AlertDialogTitle>
                        </div>
                        <button
                            onClick={handleClose}
                            disabled={isLoading}
                            className="rounded-md p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <XIcon className="h-4 w-4" />
                        </button>
                    </div>
                </AlertDialogHeader>

                <AlertDialogDescription className="text-sm text-gray-600 leading-relaxed">
                    {isSingle ? (
                        <>
                            <strong className="text-gray-900 font-medium">
                                {candidateNames[0]}
                            </strong>{" "}
                            and their application record for{" "}
                            <strong className="text-gray-900 font-medium">
                                {jobTitle}
                            </strong>{" "}
                            listing will be permanently deleted. Their candidate
                            profile is retained in your talent pool for future
                            considerations.
                        </>
                    ) : (
                        <>
                            <strong className="text-gray-900 font-medium">
                                {count} candidates&apos;
                            </strong>{" "}
                            application records for{" "}
                            <strong className="text-gray-900 font-medium">
                                {jobTitle}
                            </strong>{" "}
                            listing will be permanently deleted. Their candidate
                            profiles are retained in your talent pool for future
                            considerations.
                        </>
                    )}
                </AlertDialogDescription>

                <div className="flex items-center gap-2">
                    <Checkbox
                        id="dont-ask-again"
                        checked={dontAskAgain}
                        disabled={isLoading}
                        onCheckedChange={(checked) =>
                            setDontAskAgain(Boolean(checked))
                        }
                    />
                    <label
                        htmlFor="dont-ask-again"
                        className="text-sm text-gray-500 cursor-pointer select-none"
                    >
                        Don&apos;t show this again
                    </label>
                </div>

                <p className="text-xs text-gray-400 italic">
                    This action cannot be undone.
                </p>

                <AlertDialogFooter className="grid grid-cols-2 gap-3 sm:flex-row">
                    <Button
                        variant="outline"
                        onClick={handleClose}
                        disabled={isLoading}
                        className="w-full"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleConfirm}
                        disabled={isLoading}
                        className="w-full bg-red-500 hover:bg-red-600 text-white"
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Deleting...
                            </>
                        ) : isSingle ? (
                            "Delete Candidate"
                        ) : (
                            `Delete ${count} Candidates`
                        )}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
